import { apiClient } from './client';

/**
 * 다중 게시판 API 클라이언트 (migration 102, 2026-08-18).
 *
 * backend: apps/chat-server/src/modules/board/boards.routes.ts
 *
 * 기존 `api/board.ts`(공지·자유게시판·문의·FAQ)는 건드리지 않는다 — 그 화면(/info)은
 * 메뉴에서만 빠지고 코드는 그대로 남는다.
 *
 * 첨부 규약: 서버가 `sizeBytes`(+ 하위호환 `size`)를 함께 준다. 프론트 공용 부품
 * (AttachmentList · attachmentPreviewDoc · 미리보기 렌더러)이 전부 sizeBytes 를 읽으므로
 * 별도 변환 없이 그대로 넘길 수 있다.
 */

export type BoardType = 'CLASSIC' | 'FEED';
export type BoardScope = 'COMPANY' | 'DIVISION' | 'TEAM' | 'PERSONAL';
export type BoardGroup = 'company' | 'division' | 'team' | 'mine';
export type ShareTargetType = 'company' | 'division' | 'team' | 'user';
export type BoardMemberRole = 'OWNER' | 'MANAGER' | 'MEMBER';

export interface BoardDto {
  id: string;
  name: string;
  description: string | null;
  boardType: BoardType;
  scopeType: BoardScope;
  scopeId: string | null;
  systemKey: string | null;
  isPrivate: boolean;
  showSecretTitles: boolean;
  allowAnonymous: boolean;
  showMailButton: boolean;
  allowComments: boolean;
  usePostReaders: boolean;
  notifyEnabled: boolean;
  notifyAllByDefault: boolean;
  /**
   * 게시글 작성 허용 (migration 105). 끄면 운영자(만든 사람·지정 운영자·관리자)와
   * 공유 대상 중 작성 허용을 받은 사람만 쓴다.
   */
  allowPostWrite?: boolean;
  sortOrder: number;
  createdBy: string;
  createdAt: string;
  group?: BoardGroup;
  canManage?: boolean;
  /** 내가 이 게시판에 글을 쓸 수 있는가 (migration 105). 판정은 서버가 한다. */
  canWrite?: boolean;
  /**
   * 안 읽은 글 수 (migration 104). 서버가 **최근 30일** 안에서만 센다 —
   * 창이 없으면 옛 글이 영원히 남아 배지가 0 이 되지 않는다.
   */
  unreadCount?: number;
}

export interface BoardPrefixDto { id: string; label: string; sortOrder: number }
export interface BoardMemberDto { userId: string; role: BoardMemberRole; name: string | null; email: string | null }
export interface BoardShareDto {
  targetType: ShareTargetType; targetId: string;
  canRead: boolean; canNotify: boolean;
  /** 작성 허용 (migration 105). */
  canWrite?: boolean;
}
export interface BoardSettingsDto { defaultNotifyAll: boolean; allowUserBoardCreate: boolean; updatedAt: string | null }

export interface BoardAttachmentDto {
  fileId: string;
  name: string;
  sizeBytes: number;
  size: number;
  mimeType: string | null;
}

export interface BoardPostListItem {
  id: string;
  boardId: string | null;
  title: string;
  prefix: BoardPrefixDto | null;
  isAnonymous: boolean;
  isSecret: boolean;
  locked: boolean;
  viewCount: number;
  likeCount: number;
  commentCount?: number;
  attachmentCount: number;
  createdAt: string;
  /** 고정(핀) — 목록 맨 위 고정 영역에 걸린다 (migration 103). */
  isPinned: boolean;
  /** 고정한 시각. 고정 영역 정렬 기준. 해제하면 null. */
  pinnedAt: string | null;
  /** 내가 읽었는가 (migration 104). 내가 쓴 글은 항상 읽음이다. */
  isRead: boolean;
  /**
   * 목록 썸네일 — 첫 이미지 첨부(2026-08-19). 서버가 첨부 JSONB 에서 고른다(새 컬럼 없음).
   * 잠긴 글에는 붙지 않는다. 구버전 서버는 안 준다 — 그때는 그림 없이 그린다.
   */
  thumbnail?: { fileId: string; name: string; mimeType: string | null } | null;
  /** 검색 발췌 — 본문에서 맞은 대목 둘레. 제목만 맞았거나 잠긴 글이면 null (2026-08-19). */
  excerpt?: string | null;
  /** 통합 검색에서만 붙는 게시판 꼬리표. */
  board?: { id: string; name: string } | null;
  /**
   * 이 글을 고칠(그리고 지울) 수 있는가 (2026-08-19). **서버가 판정한다** —
   * 익명 글은 author.id 를 안 주므로 화면이 작성자 여부를 알 방법이 없고,
   * 화면이 자기 식으로 다시 계산하면 "버튼은 있는데 403" 이 된다.
   * 구버전 서버는 안 준다 — 그때는 버튼을 감추지 말고 잠근다(아래 사용처 참고).
   */
  canEdit?: boolean;
  /**
   * 확인 요청 글인가 (2026-09-16 · R1 · migration 202). 옛 서버는 안 준다 → `undefined`.
   * ★ 상태 판정은 **`pages/BoardV1/confirm-state.ts` 한 곳**에서 한다 — 목록과 글 보기가 갈라지지 않게.
   */
  requiresConfirm?: boolean;
  /** 확인한 사람 수 · 읽은 사람 수 — **확인 요청이 켜진 글에만** 실린다. */
  confirmCount?: number;
  readCount?: number;
  /**
   * 「이 글을 볼 수 있는 사람」 수(분모).
   * ★ **목록에는 실리지 않는다** — 글마다 기관 사용자를 훑어야 해서 한 장에 30번 돌 수 없다(서버 주석).
   *   상세·확인 현황에만 온다. 그래서 목록 배지는 **숫자 없이** 상태만 말한다(사용자 결정 ③).
   */
  targetCount?: number;
  /** 내가 읽은 시각 · 확인한 시각. 확인 안 했으면 `null`, 서버가 안 주면 `undefined`. */
  myReadAt?: string | null;
  myConfirmedAt?: string | null;
  /**
   * ★ `department` · `position` 은 **서버가 이미 보내고 있다**(R16 · 2026-09-16 · 마이그 없음) —
   *   `board-author-org.ts` 가 목록 한 장의 작성자를 한 번에 읽어 `displayAuthor` 가 싣는다.
   *   익명 글이면 서버가 이름과 함께 **비운다**(웹이 감추는 것이 아니다).
   *   옛 서버는 안 준다 → `undefined` 로 와도 화면이 한 줄로 그려지게 둔다.
   */
  author: {
    id: string | null;
    name: string;
    anonymous: boolean;
    department?: string | null;
    position?: string | null;
    /** 2026-10-15 — 프로필 사진(저장 꼴 그대로 · `Avatar` 가 `wcavatar:` 를 주소로 푼다). 익명이면 서버가 비운다. */
    avatarUrl?: string | null;
  };
}

export interface BoardPostDetail extends BoardPostListItem {
  content: string | null;
  contentType: 'HTML' | 'TEXT' | null;
  attachments: BoardAttachmentDto[];
  commentsAllowed: boolean;
  readers: Array<{ targetType: ShareTargetType; targetId: string }>;
}

export interface TargetInput {
  targetType: ShareTargetType;
  targetId?: string | null;
  canRead?: boolean;
  canNotify?: boolean;
  /** 작성 허용 (migration 105). 안 보내면 꺼짐이다. */
  canWrite?: boolean;
}

export interface BoardListResponse {
  boards: BoardDto[];
  settings: BoardSettingsDto;
  canCreate: boolean;
}

/**
 * 게시판 요약 — 안 읽은 글 · 주별 새 글 (R7 · `GET /boards/summary` · 2026-09-16).
 *
 * ★ `pending` 은 **서버가 아직 안 담은 칸**의 이름이다(지금은 `latestConfirm` 하나 — R1 과 함께 빠진다).
 *   화면은 이 목록에 있는 칸을 **0 이 아니라 「아직 없음」**으로 그린다 — 0 으로 그리면 「글이 하나도 없었다」는 거짓말이 된다.
 * ★ 옛 서버에는 이 라우트가 없다(404) → 부르는 쪽이 `null` 로 받아 종전처럼 그린다(아래 `getBoardSummary`).
 */
export interface BoardSummaryDto {
  unread: { total: number; byGroup: Array<{ group: 'company' | 'division' | 'team' | 'mine'; count: number }> };
  weekly: { posts: number; comments: number; series: Array<{ weekStart: string; posts: number; comments: number }> };
  pending: string[];
  window: { unreadDays: number; weeks: number; timeZone: string };
}

/**
 * 요약을 읽는다 — **실패해도 던지지 않는다**(`null`).
 *
 * 목록 화면 첫 진입마다 불리는 보조 숫자라, 이것 하나가 안 온다고 목록이 안 뜨면 안 된다.
 * 404(옛 서버) · 권한 · 네트워크 어느 쪽이든 `null` 이고, 화면은 종전 「아직 없음」으로 돌아간다.
 */
export async function getBoardSummary(weeks?: number): Promise<BoardSummaryDto | null> {
  try {
    const { data } = await apiClient.get<BoardSummaryDto>('/boards/summary', {
      params: weeks ? { weeks } : undefined,
    });
    if (!data || typeof data !== 'object') return null;
    if (!data.weekly || !Array.isArray(data.weekly.series)) return null;
    return data;
  } catch {
    return null;
  }
}

export async function listBoards(): Promise<BoardListResponse> {
  const { data } = await apiClient.get<BoardListResponse>('/boards');
  return {
    boards: Array.isArray(data?.boards) ? data.boards : [],
    settings: data?.settings ?? { defaultNotifyAll: true, allowUserBoardCreate: true, updatedAt: null },
    canCreate: data?.canCreate ?? true,
  };
}

export async function createBoard(input: {
  name: string; description?: string | null; boardType?: BoardType;
  scopeType?: 'TEAM' | 'DIVISION' | 'PERSONAL'; scopeId?: string | null;
}): Promise<BoardDto> {
  const { data } = await apiClient.post<{ board: BoardDto }>('/boards', input);
  return data.board;
}

export interface BoardDetailResponse {
  board: BoardDto;
  prefixes: BoardPrefixDto[];
  members: BoardMemberDto[];
  shares: BoardShareDto[];
}

export async function getBoard(id: string): Promise<BoardDetailResponse> {
  const { data } = await apiClient.get<BoardDetailResponse>(`/boards/${id}`);
  return data;
}

/** 변경분만 보낸다 — 서버는 빈 patch 를 400 NO_FIELDS_TO_UPDATE 로 돌려준다. */
export async function patchBoard(id: string, patch: Partial<Omit<BoardDto, 'id' | 'createdAt' | 'createdBy' | 'systemKey' | 'scopeType' | 'scopeId' | 'group' | 'canManage'>>): Promise<BoardDto> {
  const { data } = await apiClient.patch<{ board: BoardDto }>(`/boards/${id}`, patch);
  return data.board;
}

export async function deleteBoard(id: string): Promise<void> {
  await apiClient.delete(`/boards/${id}`);
}

export async function createPrefix(boardId: string, input: { label: string; sortOrder?: number }): Promise<BoardPrefixDto> {
  const { data } = await apiClient.post<{ prefix: BoardPrefixDto }>(`/boards/${boardId}/prefixes`, input);
  return data.prefix;
}

export async function deletePrefix(boardId: string, prefixId: string): Promise<void> {
  await apiClient.delete(`/boards/${boardId}/prefixes/${prefixId}`);
}

export async function addBoardMember(boardId: string, input: { userId: string; role?: BoardMemberRole }): Promise<BoardMemberDto> {
  const { data } = await apiClient.post<{ member: BoardMemberDto }>(`/boards/${boardId}/members`, input);
  return data.member;
}

export async function removeBoardMember(boardId: string, userId: string): Promise<void> {
  await apiClient.delete(`/boards/${boardId}/members/${userId}`);
}

/** 공유·알림 대상은 통째로 저장한다 — 설정 모달이 목록 하나를 통으로 보낸다. */
export async function saveBoardShares(boardId: string, targets: TargetInput[]): Promise<BoardShareDto[]> {
  const { data } = await apiClient.put<{ shares: BoardShareDto[] }>(`/boards/${boardId}/shares`, { targets });
  return Array.isArray(data?.shares) ? data.shares : [];
}

export interface BoardPostListResponse {
  posts: BoardPostListItem[];
  /**
   * 고정된 글. 서버가 **첫 장에서 아무 것도 거르지 않았을 때만** 채워 보낸다
   * (거르는 중에는 고정 글도 목록 안에 표식만 달고 섞여 나온다).
   */
  pinned: BoardPostListItem[];
  nextCursor: string | null;
}

/**
 * 글 목록 정렬 (2026-08-19). `latest` 는 커서(마지막 글 id), 조회·댓글순은 서버가 offset 으로
 * 넘긴다 — 둘 다 `nextCursor` 한 이름으로 오므로 화면은 그대로 넘겨 주기만 하면 된다.
 */
export type BoardPostSort = 'latest' | 'views' | 'comments';

export async function listBoardPosts(
  boardId: string,
  params?: { prefixId?: string; q?: string; cursor?: string; limit?: number; sort?: BoardPostSort },
): Promise<BoardPostListResponse> {
  const { data } = await apiClient.get<BoardPostListResponse>(
    `/boards/${boardId}/posts`, { params },
  );
  return {
    posts: Array.isArray(data?.posts) ? data.posts : [],
    pinned: Array.isArray(data?.pinned) ? data.pinned : [],
    nextCursor: data?.nextCursor ?? null,
  };
}

/**
 * 모든 게시판 통합 검색 (2026-08-19).
 *
 * 서버가 **읽을 수 있는 게시판만** 훑고 글 단위 판정도 한 번 더 한다. 결과 항목에는
 * 어느 게시판 글인지(`board`)와 본문 발췌(`excerpt`)가 함께 온다.
 */
export async function searchAllBoards(
  q: string,
  params?: { cursor?: string; limit?: number },
): Promise<{ posts: BoardPostListItem[]; nextCursor: string | null }> {
  const { data } = await apiClient.get<{ posts: BoardPostListItem[]; nextCursor: string | null }>(
    '/boards/search', { params: { q, ...params } },
  );
  return { posts: Array.isArray(data?.posts) ? data.posts : [], nextCursor: data?.nextCursor ?? null };
}

/**
 * 게시판의 안 읽은 글을 모두 읽음으로 (migration 104). 서버와 같은 기간 창 안에서만 찍는다.
 * 찍은 건수를 준다.
 */
export async function markBoardRead(boardId: string): Promise<number> {
  const { data } = await apiClient.post<{ marked: number }>(`/boards/${boardId}/read-all`, {});
  return data?.marked ?? 0;
}

/**
 * 글 고정·해제 (migration 103). **게시판 운영자만** 된다 — 그 외에는 서버가 403 을 준다.
 */
export async function pinBoardPost(postId: string, pinned: boolean): Promise<BoardPostListItem> {
  const { data } = await apiClient.post<{ post: BoardPostListItem }>(
    `/boards/posts/${postId}/pin`, { pinned },
  );
  return data.post;
}

export interface CreateBoardPostInput {
  title: string;
  content: string;
  contentType?: 'HTML' | 'TEXT';
  prefixId?: string | null;
  isAnonymous?: boolean;
  isSecret?: boolean;
  allowComments?: boolean | null;
  attachments?: Array<{ fileId: string; name: string; sizeBytes?: number; mimeType?: string }>;
  readers?: TargetInput[];
  notify?: { enabled?: boolean; targets?: TargetInput[] };
}

export async function createBoardPost(boardId: string, input: CreateBoardPostInput): Promise<{ post: BoardPostDetail; notified: number }> {
  const { data } = await apiClient.post<{ post: BoardPostDetail; notified: number }>(`/boards/${boardId}/posts`, input);
  return { post: data.post, notified: data.notified ?? 0 };
}

export async function getBoardPost(postId: string): Promise<{ post: BoardPostDetail; board: BoardDto }> {
  const { data } = await apiClient.get<{ post: BoardPostDetail; board: BoardDto }>(`/boards/posts/${postId}`);
  return data;
}

export async function patchBoardPost(postId: string, patch: Partial<CreateBoardPostInput>): Promise<BoardPostDetail> {
  const { data } = await apiClient.patch<{ post: BoardPostDetail }>(`/boards/posts/${postId}`, patch);
  return data.post;
}

export async function deleteBoardPost(postId: string): Promise<void> {
  await apiClient.delete(`/boards/posts/${postId}`);
}

// ── 휴지통 (2026-08-19) ────────────────────────────────────────────────────
// 글 삭제는 완전삭제가 아니라 휴지통행이고, 보존 기간이 지나면 서버가 자동으로 지운다.
// 첨부도 글과 **같은 시각**에 지워져서 복원할 때 짝을 찾아 함께 되살아난다.

export interface BoardTrashPost extends BoardPostListItem {
  /** 지운 시각. */
  deletedAt: string;
  /** 완전히 사라지기까지 남은 날. 서버가 계산해 준다 — 화면이 보존 기간을 몰라도 된다. */
  daysLeft: number;
}

export interface BoardTrashResponse {
  posts: BoardTrashPost[];
  /** 보존 기간(일). 안내 문구에 그대로 쓴다. */
  retentionDays: number;
}

/**
 * 이 게시판의 지운 글. 운영자는 전부, 나머지는 자기 글만 (판정은 서버).
 *
 * ⚠ 이 라우트를 모르는 옛 서버는 404 를 준다. 호출부는 그때 **휴지통 진입점을 감춘다** —
 *   기능이 없는 서버에서 없는 것처럼 보이게 해야 배포 순서에 안 묶인다.
 */
export async function listBoardTrash(boardId: string, limit?: number): Promise<BoardTrashResponse> {
  const { data } = await apiClient.get<BoardTrashResponse>(`/boards/${boardId}/trash`, {
    params: limit ? { limit } : undefined,
  });
  return {
    posts: Array.isArray(data?.posts) ? data.posts : [],
    retentionDays: data?.retentionDays ?? 30,
  };
}

/** 되살리기 — 같은 시각에 지워진 첨부도 함께 돌아온다. */
export async function restoreBoardPost(postId: string): Promise<{ post: BoardPostListItem; restoredAttachments: number }> {
  const { data } = await apiClient.post<{ post: BoardPostListItem; restoredAttachments: number }>(
    `/boards/posts/${postId}/restore`,
  );
  return { post: data.post, restoredAttachments: data.restoredAttachments ?? 0 };
}

/** 보존 기간을 안 기다리고 지금 완전히 지운다. 되돌릴 수 없다. */
export async function purgeBoardPost(postId: string): Promise<{ files: number; filesKept: number }> {
  const { data } = await apiClient.delete<{ files: number; filesKept: number }>(
    `/boards/posts/${postId}/purge`,
  );
  return { files: data?.files ?? 0, filesKept: data?.filesKept ?? 0 };
}

export async function notifyBoardPost(postId: string, targets?: TargetInput[]): Promise<number> {
  const { data } = await apiClient.post<{ notified: number }>(`/boards/posts/${postId}/notify`, targets ? { targets } : {});
  return data?.notified ?? 0;
}

/**
 * 「내 차례 확인 공지」 — 2026-09-16 R8 · `GET /boards/confirms/mine`.
 *
 * 목록 위 넷째 칸(확인 요청 공지)과 셋째 칸(공지 확인 도넛)이 이 값 위에 선다.
 *
 * ★★ `pendingCount` 와 `items.length` 는 **일부러 다르다** — 목록은 최근 몇 건만 오고,
 *    세는 것은 전부다. **목록 길이로 배지를 그리면 틀린다**(서버 주석의 경고 그대로).
 * ★ 옛 서버(404)·권한·네트워크 어느 쪽이든 `null` 을 준다 — 부르는 쪽이 「아직 없음」을 세운다.
 *   목록 화면은 이 값 없이도 그대로 뜬다(요약과 같은 판단).
 */
export interface BoardConfirmMineItem {
  postId: string;
  boardId: string | null;
  board: { id: string; name: string } | null;
  title: string;
  author: { id: string | null; name: string; anonymous: boolean };
  postedAt: string;
  /** **내가** 확인한 시각. 안 눌렀으면 null — 이 값이 null 인 것만 `pendingCount` 에 든다. */
  confirmedAt: string | null;
}

export interface BoardConfirmsMineDto {
  pendingCount: number;
  items: BoardConfirmMineItem[];
}

export async function getConfirmsMine(limit?: number): Promise<BoardConfirmsMineDto | null> {
  try {
    const { data } = await apiClient.get<BoardConfirmsMineDto>('/boards/confirms/mine', {
      params: limit ? { limit } : undefined,
    });
    if (!data || typeof data !== 'object' || !Array.isArray(data.items)) return null;
    return { pendingCount: data.pendingCount ?? 0, items: data.items };
  } catch {
    return null;
  }
}
